import React, { useState, useEffect } from 'react';
import { FileText, AlertCircle, ChevronDown, ChevronUp, X } from 'lucide-react';
import { getFeatures, getTermsV70AtMs, TERMS_V70_NOTICE_AT_MS, getTermsV71AtMs, getTermsV80NoticeAtMs, getTermsV80AtMs } from '../utils/features';
import {
    TERMS_V70_NOTICE_DATE, TERMS_V70_EFFECTIVE_DATE, V70_DEFINITIONS, V70_SERVICE_ITEM, V70_CLINIC_INCLUDED,
    V70_ART4_7, V70_ART4_8, V70_ART4_9, V70_INFOBOX_EXTRA, V70_APPENDIX_PARA, V70_HISTORY_LINE,
} from '../config/termsV70';
import {
    TERMS_V71_NOTICE_AT_MS, V71_DEFINITIONS, V71_ART4_6_TITLE, V71_ART4_6_P1_PAREN_ITEM, V71_ART4_6_P1_PROVISO, V71_ART4_6_P3_ADD, V71_ART4_6_P4_ADD,
    V71_ART4_10, V71_ART4_11, V71_ART4_12, V71_APPENDIX_PARA, V71_HISTORY_LINE,
    V71_NOTICE_HEAD_BEFORE, V71_NOTICE_HEAD_AFTER, V71_NOTICE_BODY, V71_VERSION_NOTICE_LINE, V71_VERSION_EFFECTIVE_LINE,
} from '../config/termsV71';   // v7.1 CNC 파트너 개정 — 정본 docs/약관_v7.1_개정안_CNC파트너_2026-10-24.md (1차 필터·변호사 최종)
import { buildTermsVersions, currentTermsVersion, versionButtonLabel, versionLine } from '../config/termsVersions';   // 판 목록(v6.2·v7.0·v7.1·v8.0) — 버튼·상자·버전 상자·이력이 이 목록을 순회
import {
    V80_ART1, V80_ART2, V80_ART3, V80_ART4, V80_ART4_2, V80_ART4_3, V80_ART4_4, V80_DELETED_ARTICLES, V80_ART4_6,
    V80_ART4_10, V80_ART4_10_2, V80_ART4_12, V80_ART4_13, V80_ART4_14, V80_ART4_15, V80_ART4_16, V80_ART4_17, V80_ART4_18,
    V80_ART5, V80_ART9, V80_ART10, V80_ART11, V80_ART12, V80_ART13,
    V80_APPENDIX_P1_BASE, V80_APPENDIX_P2, V80_APPENDIX_P3, V80_APPENDIX_P4, V80_APPENDIX_P5_TITLE, v80AppendixPara, v80HistoryLine,
    V80_NOTICE_BODY, v80NoticeHeadBefore, v80NoticeHeadAfter, v71ReplacedLine, v80VersionNoticeLine, v80VersionEffectiveLine,
} from '../config/termsV80';   // v8.0 전면 정비 — 정본 docs/약관_v8.0_전문_초안_2026-10-24.md. 공고 전: config/features.termsV80NoticeAt·termsV80At 가 없으면 어디에도 안 그린다
import { isPrivacyDesignOn } from './PrivacyPolicy';   // 완성 디자인 A — 제4조의7 "완성 산출물 보관" 문구는 시행일(2026-10-21) 전엔 숨김(fail-closed). 동의 해시(V70_ART4_7_TEXT)는 변경하지 않음.

// v6.2 공고일 — 구독료 개정(치과 99,000원·기공소 33,000원, 신규 체험 1개월). 계획 docs/계획_요금변경_2026-10-04.md §4-5
//   공고일 2026-10-04 확정(대표 결정). 서버 config/features.pricingV62At(신규 요금표 기록 시작 시각)과 같은 날이어야 한다.
const TERMS_V62_DATE = '2026년 10월 4일';

// v7.0 — 환자관리 서비스·제4조의7 전면 개정 (공고 2026-10-06 · 시행 2026-11-06, 대표 결정 10-06). 조문 문자열은 src/config/termsV70.js.
//   두 판 전환: 시행 시각(config/features.termsV70At, 기본 2026-11-06 00:00 KST — utils/features.js getTermsV70AtMs) 전에는 v6.2 가 현행,
//   v7.0 은 "개정 예정(공고 중)" 으로 같은 화면에서 전환해 전문을 읽을 수 있다(30일 공고 = 전문 게시). 시행 후에는 v7.0 이 현행, v6.2 는 "종전 약관" 으로 열람.
// v7.1 — CNC 파트너(제조업체·영업업체·회사 재판매) 개정 (공고 2026-10-24 · 시행 2026-11-24). 조문 문자열은 src/config/termsV71.js.
//   세 판 전환: v7.1 판 = v7.0 전문 + v7.1 추가 조문(제2조 정의 2항목, 제4조의6 추가 문장, 제4조의10·11·12, 부칙 문단, 이력 줄). v7.0 판·v6.2 판에는 v7.1 조문이 보이지 않는다.
//   시행 시각 config/features.termsV71At(기본 2026-11-24 00:00 KST — getTermsV71AtMs). 공고일 전에는 v7.1 상자·버튼 자체를 숨긴다(isTermsV71Announced).
//   기본 판: v7.1 시행 후 → v7.1, 그 전 v7.0 시행 후 → v7.0, 그 전 → v6.2. v7.0 상자와 v7.1 상자가 함께 보이는 기간(10-24~11-06, 이후도 두 상자 유지)은 위 v7.1·아래 v7.0 으로 세로 나열.
// v8.0 — 전면 정비 (공고 전. 조문·문안 src/config/termsV80.js, 판 목록 src/config/termsVersions.js).
//   공고일·시행일은 config/features.termsV80NoticeAt·termsV80At 로만 정해진다(기본값 없음). 값이 없으면 v8.0 상자·버튼·조문·이력 전부 숨김(기존 세 판 동작 그대로).
//   값이 있고 공고일 ≤ 지금 < 시행일 → 「개정 예정 v8.0」 상자·버튼, 시행 후 → v8.0 현행. v8.0 판은 자체 전문(V80Articles)을 그린다 — 제4조의7·8·9 는 termsV70.js 객체 그대로, 제4조의11 은 termsV71.js 그대로.
//   v7.1 처리: v8.0 공고가 시작되면 v7.1 상자를 숨기고 그 자리에 v8.0 상자 + 「v7.1 대체 1줄」(공고일이 v7.1 시행 전일 때 ⓐ). v7.1 버튼은 「v7.1 공고 문안 보기」로 남긴다(공고 증빙). v8.0 공고 전에는 v7.1 상자 그대로.
//   판 목록(TERMS_VERSIONS) = buildTermsVersions() — 버튼·상자·버전 상자·이력이 이 목록을 순회한다. 기존 v6.2/v7.0/v7.1 표시는 바뀌지 않아야 한다.

function TermsOfService({ onClose }) {
    const [expandedSections, setExpandedSections] = useState({});
    // 훅은 전부 early return 위 (Dashboard 사고 교훈)
    const [features, setFeatures] = useState(null);
    const [viewVer, setViewVer] = useState(null);   // null = 자동(시행 여부), 'v62' | 'v70' | 'v71' | 'v80' = 사용자가 고른 판
    useEffect(() => {
        let alive = true;
        getFeatures()
            .then(f => { if (alive) setFeatures(f || {}); })
            .catch(e => { console.error('[약관] 플래그 읽기 실패 — 기본 시행일 사용:', e?.message || e); if (alive) setFeatures({}); });
        return () => { alive = false; };
    }, []);
    const nowMs = Date.now();
    // 판 목록 — 공고일 전(빌드만 먼저 나간 경우)에는 그 판의 전환 UI·상자·조문을 숨긴다(announced=false). v8.0 은 features 값이 없으면 announced=false.
    const TERMS_VERSIONS = buildTermsVersions({
        nowMs,
        v70NoticeAtMs: TERMS_V70_NOTICE_AT_MS, v70AtMs: getTermsV70AtMs(features),
        v71NoticeAtMs: TERMS_V71_NOTICE_AT_MS, v71AtMs: getTermsV71AtMs(features),
        v80NoticeAtMs: getTermsV80NoticeAtMs(features), v80AtMs: getTermsV80AtMs(features),
    });
    const ver = Object.fromEntries(TERMS_VERSIONS.map(v => [v.id, v]));
    const autoVer = currentTermsVersion(TERMS_VERSIONS).id;
    const curVer = (viewVer !== null && ver[viewVer]?.announced) ? viewVer : autoVer;
    const v70Announced = ver.v70.announced;
    const v70Effective = ver.v70.effective;
    const v71Announced = ver.v71.announced;   // (v7.1 시행 여부는 ver.v71.effective — v8.0 공고로 대체(replaced)되면 시행 시각이 지나도 false. 라벨·버전 줄은 termsVersions 가 계산)
    const v71Replaced = ver.v71.replaced;
    const v80Announced = ver.v80.announced;
    const v80Effective = ver.v80.effective;
    const showV80 = v80Announced && curVer === 'v80';       // v8.0 판은 자체 전문을 그린다(아래 V80Articles). 다른 판에는 v8.0 조문이 전혀 보이지 않는다
    const showV71 = v71Announced && curVer === 'v71';       // v7.1 추가 조문 표시(공고 전엔 어떤 경로로도 false)
    const showV70 = curVer === 'v70' || showV71;            // v7.1 판은 v7.0 전문을 기준본으로 하므로 v7.0 조문도 함께 표시

    const toggleSection = (sectionId) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId]
        }));
    };

    return (
        <div style={styles.overlay}>
            <div style={styles.modal}>
                {/* 헤더 */}
                <div style={styles.header}>
                    <div style={styles.headerTitle}>
                        <FileText size={24} color="#6366f1" />
                        <h2 style={styles.title}>이용약관</h2>
                    </div>
                    {onClose && (
                        <button onClick={onClose} style={styles.closeButton}>
                            <X size={24} />
                        </button>
                    )}
                </div>

                {/* 내용 */}
                <div style={styles.content}>
                    {/* 서문 */}
                    <div style={styles.intro}>
                        <p style={styles.introText}>
                            주식회사 덴탈클러스터(이하 "회사")가 제공하는 서비스를 이용해 주셔서 감사합니다.
                            본 약관은 회사가 제공하는 DentalCluster 서비스(이하 "서비스")의 이용과 관련하여
                            회사와 이용자 간의 권리, 의무 및 책임사항을 규정합니다.
                        </p>
                    </div>

                    {/* v8.0 공고 상자 (config/features.termsV80NoticeAt·termsV80At 둘 다 있고 공고일 이후에만 — 맨 위. 문안 termsV80.js = 정본 §4-1) */}
                    {v80Announced && (
                        <div style={styles.noticeBox}>
                            <p style={{ margin: '0 0 8px', fontWeight: 700, color: '#1e3a8a' }}>
                                {v80Effective ? v80NoticeHeadAfter(features) : v80NoticeHeadBefore(features)}
                            </p>
                            <p style={{ margin: 0, fontSize: 13, color: '#334155' }}>
                                {V80_NOTICE_BODY}
                            </p>
                        </div>
                    )}
                    {/* v7.1 상자 대체 1줄 — v8.0 공고가 v7.1 시행(11-24) 전에 시작된 경우(ⓐ). 문안 정본 §4-1 「v7.1 상자 처리」 */}
                    {v80Announced && v71Replaced && (
                        <div style={styles.noticeBox}>
                            <p style={{ margin: 0, fontSize: 13, color: '#334155' }}>{v71ReplacedLine(features)}</p>
                        </div>
                    )}

                    {/* v7.1 공고 상자 (공고일 2026-10-24 이후, v8.0 공고 시작 전까지 — v7.0 상자 위에 세로 나열. 문안 termsV71.js = 정본 §4 공고 상자). v8.0 공고가 시작되면 숨기고 그 자리에 v8.0 상자 */}
                    {v71Announced && !v80Announced && (
                        <div style={styles.noticeBox}>
                            <p style={{ margin: '0 0 8px', fontWeight: 700, color: '#1e3a8a' }}>
                                {ver.v71.effective ? V71_NOTICE_HEAD_AFTER : V71_NOTICE_HEAD_BEFORE}
                            </p>
                            <p style={{ margin: 0, fontSize: 13, color: '#334155' }}>
                                {V71_NOTICE_BODY}
                            </p>
                        </div>
                    )}

                    {/* v7.0 공고 상자 (공고일 이후에만 표시) */}
                    {v70Announced && (
                        <div style={styles.noticeBox}>
                            <p style={{ margin: '0 0 8px', fontWeight: 700, color: '#1e3a8a' }}>
                                {v70Effective
                                    ? `이용약관 v7.0이 ${TERMS_V70_EFFECTIVE_DATE}부터 시행 중입니다(${TERMS_V70_NOTICE_DATE} 공고).`
                                    : `이용약관 v7.0 개정을 ${TERMS_V70_NOTICE_DATE}에 공고했습니다. 시행일 ${TERMS_V70_EFFECTIVE_DATE}까지는 현행 v6.2가 적용됩니다.`}
                            </p>
                            <p style={{ margin: 0, fontSize: 13, color: '#334155' }}>
                                {'주요 변경: 치과 환자관리 서비스 신설(제4조의8), 환자 대상 알림 범위(제4조의9), 환자 개인정보 처리위탁 조항 전면 개정(제4조의7), 치과 회원 구독 포함 범위에 환자관리 서비스 추가(구독료 변경 없음). 개정 약관에 동의하지 않으시면 시행일 전에 탈퇴할 수 있습니다(제13조 제3항).'}
                            </p>
                        </div>
                    )}

                    {/* 판 전환 버튼 — 판 목록을 순회(공고된 판만). v7.0 공고일 이후 표시. 라벨 규칙 termsVersions.versionButtonLabel(종전/현행/개정 예정, v7.1 대체 시 「공고 문안 보기」) */}
                    {v70Announced && (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                            {TERMS_VERSIONS.filter(v => v.announced).map(v => (
                                <button key={v.id} type="button" onClick={() => setViewVer(v.id)} style={curVer === v.id ? styles.verBtnActive : styles.verBtn}>
                                    {versionButtonLabel(TERMS_VERSIONS, v)}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* 버전 상자 — 현재 보는 판 것 하나. 「버전:」 줄은 termsVersions.versionLine(종전/현행/예정 규칙 공통) */}
                    {showV80 ? (
                        <div style={styles.versionBox}>
                            <p><strong>공고일자:</strong> {v80VersionNoticeLine(features)}</p>
                            <p><strong>시행일자:</strong> {v80VersionEffectiveLine(features)}</p>
                            <p><strong>버전:</strong> {versionLine(TERMS_VERSIONS, ver.v80)}</p>
                        </div>
                    ) : showV71 ? (
                        <div style={styles.versionBox}>
                            <p><strong>공고일자:</strong> {V71_VERSION_NOTICE_LINE}</p>
                            <p><strong>시행일자:</strong> {V71_VERSION_EFFECTIVE_LINE}</p>
                            <p><strong>버전:</strong> {versionLine(TERMS_VERSIONS, ver.v71)}</p>
                        </div>
                    ) : showV70 ? (
                        <div style={styles.versionBox}>
                            <p><strong>공고일자:</strong> {`${TERMS_V70_NOTICE_DATE} (v7.0 개정 — 치과 환자관리 서비스 신설(제4조의8), 환자 대상 알림 범위(제4조의9), 환자 개인정보 처리위탁 조항 전면 개정(제4조의7), 치과 회원 구독 포함 범위에 환자관리 서비스 추가(구독료 변경 없음))`}</p>
                            <p><strong>시행일자:</strong> {`${TERMS_V70_EFFECTIVE_DATE} (공고일부터 30일 경과 후 시행 — 제13조 제2항). 시행일 이후 신규 가입하는 회원에게는 가입 시 동의로 즉시 적용합니다. 기존 회원의 환자관리 서비스(제4조의7 제1항 나목부터 사목까지)는 제4조의7 제14항에 따라 처음 진입할 때 동의를 다시 확인한 뒤 개방합니다. v6.2 개정 조항의 적용 시기는 부칙 제1항에 따릅니다.`}</p>
                            <p><strong>버전:</strong> {versionLine(TERMS_VERSIONS, ver.v70)}</p>
                        </div>
                    ) : (
                        <div style={styles.versionBox}>
                            <p><strong>공고일자:</strong> {`${TERMS_V62_DATE} (v6.2 개정 — 구독료 개정: 치과 회원 월 99,000원·기공소 회원 월 33,000원(부가세 포함), 신규 가입 무료 체험 1개월, 제4조의2)`}</p>
                            <p><strong>시행일자:</strong> {`${TERMS_V62_DATE} 이후 신규 가입하는 회원에게는 가입 시 동의로 공고일부터 적용합니다. ${TERMS_V62_DATE} 전에 가입한 회원의 구독료와 회사가 부여한 무료 이용 기간은 변경되지 않습니다(제4조의2 제6항 경과 규정). 결제 실패·이용 중지 시 덴클 프로그램 중지(제4조의2 제3항)는 기존 회원에게는 2026년 11월 3일부터 적용합니다.`}</p>
                            <p><strong>버전:</strong> {versionLine(TERMS_VERSIONS, ver.v62)}</p>
                        </div>
                    )}

                    {/* v8.0 판 = 자체 전문(아래 V80Articles). 그 외 판 = 기존 v6.2 본문 + v7.0/v7.1 조건부 조문 */}
                    {showV80 ? (
                        <V80Articles features={features} expandedSections={expandedSections} toggleSection={toggleSection} v71Replaced={v71Replaced} />
                    ) : (<>
                    {/* 제1조 */}
                    <Section
                        id="section1"
                        title="제1조 (목적)"
                        expanded={expandedSections.section1}
                        onToggle={() => toggleSection('section1')}
                    >
                        <p style={styles.text}>
                            본 약관은 주식회사 덴탈클러스터(이하 "회사")가 제공하는 치과-치과기공소 간 업무 관리,
                            마켓플레이스, 중고물품 거래, 광고, 마케팅 대행 서비스 등(이하 "서비스")의 이용과 관련하여
                            회사와 이용자 간의 권리, 의무 및 책임사항, 기타 필요한 사항을 규정함을 목적으로 합니다.
                        </p>
                    </Section>

                    {/* 제2조 */}
                    <Section
                        id="section2"
                        title="제2조 (정의)"
                        expanded={expandedSections.section2}
                        onToggle={() => toggleSection('section2')}
                    >
                        <p style={styles.text}>본 약관에서 사용하는 용어의 정의는 다음과 같습니다:</p>
                        <ul style={styles.list}>
                            <li><strong>"서비스"</strong>란 회사가 제공하는 치과-기공소 업무 관리, 마켓플레이스, 중고물품 거래, 광고, 세미나, 구인공고, 마케팅 대행 등 모든 관련 서비스를 의미합니다.</li>
                            <li><strong>"회원"</strong>이란 본 약관에 동의하고 서비스에 가입하여 이용하는 자를 말하며, 다음과 같이 구분됩니다:
                                <ul style={styles.subList}>
                                    <li><strong>개인회원:</strong> 개인 자격으로 가입한 이용자</li>
                                    <li><strong>치과회원:</strong> 치과의원/병원으로 가입한 이용자</li>
                                    <li><strong>기공소회원:</strong> 치과기공소로 가입한 이용자</li>
                                    <li><strong>판매자회원:</strong> 마켓플레이스에서 상품을 판매하는 이용자</li>
                                    <li><strong>마케터회원:</strong> 마케팅 대행 서비스를 제공하는 이용자</li>
                                </ul>
                            </li>
                            <li><strong>"스토어"</strong>란 판매자회원이 마켓플레이스에서 운영하는 온라인 매장을 의미합니다.</li>
                            <li><strong>"콘텐츠"</strong>란 회원이 서비스 내에서 게시하는 글, 사진, 파일, 링크 등 모든 형태의 정보를 의미합니다.</li>
                            {/* v7.0 신설 정의 2항목 */}
                            {showV70 && V70_DEFINITIONS.map((t) => <li key={t.slice(0, 12)}>{t}</li>)}
                            {/* v7.1 신설 정의 2항목 — 제조업체회원·영업업체회원 */}
                            {showV71 && V71_DEFINITIONS.map((t) => <li key={t.slice(0, 12)}>{t}</li>)}
                        </ul>
                    </Section>

                    {/* 제3조 */}
                    <Section
                        id="section3"
                        title="제3조 (서비스의 제공)"
                        expanded={expandedSections.section3}
                        onToggle={() => toggleSection('section3')}
                    >
                        <p style={styles.text}>회사는 다음과 같은 서비스를 제공합니다:</p>
                        
                        <h4 style={styles.subTitle}>가. 기본 운영 서비스 (유료 구독)</h4>
                        <ul style={styles.list}>
                            <li>치과 기공 의뢰서 작성 및 관리</li>
                            <li>거래명세서 자동 생성 및 관리</li>
                            <li>거래처(치과·기공소) 연결 및 단가표 관리</li>
                            <li>매출·리메이크율 통계 및 경영분석</li>
                            <li>실시간 채팅 및 파일 공유</li>
                            <li>캘린더 일정 관리</li>
                            <li>전자세금계산서 발행 (팝빌 연동)</li>
                            <li>외주 발주·수주 관리</li>
                            {/* v7.0 추가 — 치과 환자관리 */}
                            {showV70 && <li>{V70_SERVICE_ITEM}</li>}
                        </ul>

                        <h4 style={styles.subTitle}>나. 기공소 홍보 대행 서비스 (유료 구독)</h4>
                        <ul style={styles.list}>
                            <li>덴탈아리랑·치과신문 등 치과 전문 매체 배너 광고 게재</li>
                            <li>전국 치과 직접 방문 영업 및 전화 영업</li>
                            <li>인스타그램 등 SNS 광고 집행 (치과 원장·실장 타겟)</li>
                            <li>기공소 찾기 페이지 공개 및 지역별 우선 노출</li>
                            <li>실시간 유입 통계 및 지역별 분석 리포트 제공</li>
                        </ul>

                        <h4 style={styles.subTitle}>다. 치과 홍보 대행 서비스 (유료 구독)</h4>
                        <ul style={styles.list}>
                            <li>네이버 카페 바이럴 콘텐츠 작성·발행 (월 20회+ 보장)</li>
                            <li>인스타그램·메타 광고 집행 (월 5만회+ 노출 보장)</li>
                            <li>치과 찾기 페이지 공개 및 환자 지역 기반 우선 노출</li>
                            <li>치과 프로필 페이지 운영 (의료인·시설·장비·진료과목)</li>
                            <li>실시간 유입 통계 및 환자 위치 분석 리포트 제공</li>
                        </ul>

                        <h4 style={styles.subTitle}>라. 환자 리뷰 시스템</h4>
                        <ul style={styles.list}>
                            <li>QR 코드 기반 환자 익명 리뷰 수집</li>
                            <li>의료법 광고심의가 필요 없는 선택형 태그 방식 운영</li>
                            <li>친절·소통, 위생·청결, 진료·전문성 등 8개 카테고리 40여 개 태그 제공</li>
                            <li>치과·기공소가 직접 리뷰 표시 여부를 관리</li>
                        </ul>

                        <h4 style={styles.subTitle}>마. 마켓플레이스 / 중고물품 / 구인공고 서비스</h4>
                        <ul style={styles.list}>
                            <li>치과 재료, 장비, 소모품 등 상품 판매/구매 (판매자 스토어)</li>
                            <li>중고 치과/기공 장비 등록 및 거래 중개</li>
                            <li>구인공고 게시 및 신제품·세미나 정보 제공</li>
                            <li>결제 서비스 (포트원·토스페이먼츠 연동)</li>
                        </ul>
                    </Section>

                    {/* 제4조 */}
                    <Section
                        id="section4"
                        title="제4조 (회원가입 및 자격)"
                        expanded={expandedSections.section4}
                        onToggle={() => toggleSection('section4')}
                    >
                        <p style={styles.text}>
                            1. 서비스를 이용하고자 하는 자는 본 약관에 동의하고 회원가입 절차를 완료해야 합니다.
                        </p>
                        <p style={styles.text}>
                            2. 판매자회원으로 활동하기 위해서는 별도의 판매자 승인 절차를 거쳐야 하며,
                            사업자등록증 등 필요한 서류를 제출해야 합니다.
                        </p>
                        <p style={styles.text}>
                            3. 회사는 다음 각 호에 해당하는 경우 가입을 거부하거나 회원 자격을 제한할 수 있습니다:
                        </p>
                        <ul style={styles.list}>
                            <li>타인의 정보를 도용한 경우</li>
                            <li>허위 정보를 기재한 경우</li>
                            <li>관련 법령을 위반한 경우</li>
                            <li>이전에 회원 자격을 상실한 자</li>
                        </ul>
                    </Section>

                    {/* 제4-2조: 구독 요금 */}
                    <Section
                        id="section4_2"
                        title="제4조의2 (유료 구독 요금)"
                        expanded={expandedSections.section4_2}
                        onToggle={() => toggleSection('section4_2')}
                    >
                        <p style={styles.text}>
                            1. 회사가 제공하는 운영 기능 및 홍보 대행 서비스는 월정액 구독제로 운영되며,
                            요금은 다음과 같습니다.
                        </p>
                        <div style={styles.tableBox}>
                            <table style={styles.table}>
                                <thead>
                                    <tr style={styles.tableHeaderRow}>
                                        <th style={styles.tableHeader}>구분</th>
                                        <th style={styles.tableHeader}>월 구독료</th>
                                        <th style={styles.tableHeader}>포함 내용</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr style={styles.tableRow}>
                                        <td style={styles.tableCell}><strong>기공소 회원</strong></td>
                                        <td style={styles.tableCell}>월 33,000원<br/>(부가세 포함)<br/><span style={{ fontSize: 12, color: '#059669' }}>{'가입 시 1개월 무료 체험(사업자당 1회)'}</span><br/><span style={{ fontSize: 12, color: '#64748b' }}>{`(${TERMS_V62_DATE} 전 가입 기공소는 회사가 안내한 무료 이용 기간 유지)`}</span></td>
                                        <td style={styles.tableCell}>거래명세서 자동화 + 세금계산서 + 운영 기능</td>
                                    </tr>
                                    <tr style={styles.tableRow}>
                                        <td style={styles.tableCell}><strong>치과 회원</strong></td>
                                        <td style={styles.tableCell}>월 99,000원<br/>(부가세 포함)<br/><span style={{ fontSize: 12, color: '#059669' }}>{'가입 시 1개월 무료 체험(사업자당 1회)'}</span><br/><span style={{ fontSize: 12, color: '#64748b' }}>{`(${TERMS_V62_DATE} 전 가입 치과는 별도 통지 시까지 구독료 면제)`}</span></td>
                                        <td style={styles.tableCell}>{showV70 ? V70_CLINIC_INCLUDED : '의뢰서 작성·주문 현황·명세서/계산서 확인 등 운영 기능'}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <p style={styles.text}>
                            2. 구독료는 첫 결제일부터 매월 같은 날 자동 결제되며(해당 날짜가 없는 달은 그 달의 말일), 약정 기간은 없습니다.
                            무료 체험이 있는 경우 첫 결제일은 체험 종료일의 다음 날입니다.
                            회원은 언제든지 해지할 수 있으며, 해지하더라도 이미 결제한 이용 기간이 끝날 때까지 서비스를 이용할 수 있습니다.
                        </p>
                        <p style={styles.text}>
                            3. 결제수단의 한도 초과·잔액 부족·카드 만료 등으로 결제가 실패한 경우,
                            결제일부터 7일 동안은 서비스를 그대로 이용할 수 있고, 회사는 그동안 결제를 다시 시도합니다. 그 기간 내 결제가 완료되지 않으면 유료 기능 이용이 자동 중지됩니다.
                            {` 이용이 중지된 동안(결제일부터 7일이 지나도록 결제되지 않거나, 무료 체험·이용 기간이 끝난 뒤 결제가 없는 경우)에는 PC에 설치한 덴클 프로그램의 자료 수집과 파일 주고받기도 멈추며, 결제가 확인되면 다시 시작됩니다. 다시 시작하면 멈춘 동안 새로 생기거나 바뀐 파일을 이어서 수집하지만, 그사이 지우거나 옮긴 파일, 평소 자동 수집 범위를 벗어난 파일, 서버 보관 기간(제4조의7 제3항)이 지난 전송 파일, 요청 후 24시간이 지난 'CAD로 보내기' 요청은 처리되지 않을 수 있습니다. 회사는 이용 중지를 이유로 회원 PC의 파일이나 이미 저장된 자료를 삭제하지 않습니다(전송용 파일은 제4조의7 제3항에 따라 자동 삭제되며, 그 밖에 이 약관과 개인정보처리방침에서 정한 보관 기간·삭제 기준은 그대로 적용됩니다). 이용이 중지된 동안 자료 삭제를 원하시면 고객센터(010-2972-3733)로 요청하실 수 있으며, 회사는 회원 본인인지 확인한 뒤 처리하고 결과를 알려드립니다.`}
                        </p>
                        <p style={styles.text}>
                            4. 회사는 30일 전 사전 고지 후 구독료를 변경할 수 있으며, 변경된 요금은 다음 결제 주기부터 적용됩니다.
                            기존 회원이 변경된 요금에 동의하지 않을 경우 해지를 통해 거부 의사를 표시할 수 있습니다.
                        </p>
                        <p style={styles.text}>
                            {`5. 치과 회원 구독의 적용: ${TERMS_V62_DATE} 이후 신규 가입하는 치과 회원에게는 가입 시 1개월 무료 체험 후 치과 회원 구독(월 99,000원, 부가세 포함)이 적용되며, 구독하지 않는 경우 일부 기능(도착 예정·작업 공정·최근 스캔·받은 명세서·세금계산서·정산 확인·기공비 분석·재제작 분석·덴클 프로그램)의 이용이 제한될 수 있습니다. 의뢰서 작성·보낸 의뢰서 확인은 구독 여부와 관계없이 무료로 제공합니다. 일부 회원은 회사 정책에 따라 구독료의 전부 또는 일부를 면제받을 수 있습니다.`}
                        </p>
                        {/* v6.2 신설 — 경과 규정 (계획 §4-5 제5항의2, 법무검토 ④ 경과 규정 문구를 대표 결정에 맞게 수정) */}
                        <p style={styles.text}>
                            {`6. 경과 규정: 이 약관 v6.2의 구독료 규정은 ${TERMS_V62_DATE} 이후 가입하는 회원에게는 가입 시 동의로 공고일부터 적용합니다. ${TERMS_V62_DATE} 전에 가입한 치과 회원에게는 회사가 별도로 통지할 때까지 구독료를 받지 않으며, 이를 변경하려는 경우 30일 전까지 변경 전후 금액·첫 결제일·해지 방법을 개별 통지하고 결제 화면에서 동의를 받은 경우에만 결제합니다. ${TERMS_V62_DATE} 전에 가입한 기공소 회원의 구독료와 회사가 부여한 무료 이용 기간은 변경되지 않습니다.`}
                        </p>
                        <p style={styles.text}>
                            7. 무료 체험은 회원(사업자)당 1회 제공합니다.
                        </p>
                        <p style={styles.text}>
                            8. 구독료 결제는 회원이 결제 화면에서 금액·첫 결제일·자동결제 조건을 확인하고 동의한 경우에만 이루어집니다.
                        </p>
                        <p style={styles.text}>
                            9. 회사는 프로모션 기간 동안 구독료의 전부 또는 일부를 면제할 수 있습니다.
                            매칭·중개 서비스와 관련한 이용료·수수료는 제4조의5에 따라 받지 않습니다(무료).
                        </p>
                    </Section>

                    {/* 제4-3조: 환불 및 청약철회 */}
                    <Section
                        id="section4_3"
                        title="제4조의3 (청약철회 및 환불)"
                        expanded={expandedSections.section4_3}
                        onToggle={() => toggleSection('section4_3')}
                    >
                        <p style={styles.text}>
                            1. <strong>청약철회 (전자상거래법 제17조)</strong>: 회원은 유료 구독 결제일로부터 7일 이내에는
                            서비스를 한 번도 이용하지 않은 경우에 한하여 청약을 철회하고 전액 환불받을 수 있습니다.
                        </p>
                        <p style={styles.text}>
                            2. <strong>이용 후 해지</strong>: 서비스를 1회 이상 이용한 경우
                            해당 월 구독료는 환불되지 않으며, 해지 신청은 다음 결제 주기부터 적용됩니다.
                        </p>
                        <p style={styles.text}>
                            3. <strong>중도 해지</strong>: 회원의 단순 변심으로 인한 중도 해지의 경우 일할 계산 환불은 제공되지 않습니다.
                        </p>
                        <p style={styles.text}>
                            4. <strong>회사 귀책 환불</strong>: 회사의 중대한 시스템 장애로 7일 이상 서비스가 중단된 경우,
                            회원은 해당 기간에 대한 일할 환불을 요청할 수 있습니다.
                        </p>
                        <p style={styles.text}>
                            5. <strong>직접 환불</strong>: 회원은 결제일부터 7일 이내에는 구독 화면에서 직접 그 결제의 전액 환불을 신청할 수 있으며, 이때는 서비스 이용 여부를 묻지 않습니다. 직접 환불을 하면 그 결제로 받은 이용 기간은 즉시 끝납니다. 화면에서 하는 직접 환불은 계정당 1회 제공합니다. 이후의 환불·청약철회는 제6항에 따라 고객센터에서 받으며, 제1항부터 제4항까지의 기준과 관계 법령에 따라 처리합니다. 이 항은 「전자상거래 등에서의 소비자보호에 관한 법률」에 따른 회원의 청약철회 권리를 제한하지 않습니다.
                        </p>
                        <p style={styles.text}>
                            6. 환불·청약철회 신청은 고객센터(010-2972-3733 / dentcluster@gmail.com / 카카오톡 @덴탈클러스터)를 통해 접수되며,
                            확인 후 7영업일 이내에 결제 수단으로 환불됩니다.
                        </p>
                    </Section>

                    {/* 제4-4조: 의료광고 책임 */}
                    <Section
                        id="section4_4"
                        title="제4조의4 (의료광고 및 콘텐츠 책임)"
                        expanded={expandedSections.section4_4}
                        onToggle={() => toggleSection('section4_4')}
                    >
                        <p style={styles.text}>
                            1. 회사가 회원(치과·기공소)을 위해 제작·발행하는 모든 광고 콘텐츠는
                            「의료법」 제56조(의료광고의 금지 등), 「의료법 시행령」 제23조,
                            「표시·광고의 공정화에 관한 법률」을 준수하여 작성됩니다.
                        </p>
                        <p style={styles.text}>
                            2. 의료광고 사전심의 대상에 해당하는 콘텐츠는 대한치과의사협회 등 관련 기관의 사전심의를
                            거친 후 게재됩니다. 회원은 광고 콘텐츠 발행 전 회사가 송부하는 시안을 반드시 검토·승인해야 하며,
                            승인 후 발행된 콘텐츠로 인한 행정처분·민사상 책임은 회원이 부담합니다.
                        </p>
                        <p style={styles.text}>
                            3. 환자 리뷰 시스템은 회원이 직접 작성하는 후기형·체험형 광고가 아닌
                            <strong> 선택형 태그 기반의 만족도 조사 시스템</strong>으로 운영되어 의료광고 사전심의 대상에서 제외됩니다.
                            그러나 회원이 자체적으로 리뷰 화면을 캡처하여 외부 광고에 사용하는 경우의 책임은 해당 회원이 부담합니다.
                        </p>
                        <p style={styles.text}>
                            4. 회원은 회사가 제작한 콘텐츠와 별개로 자체 작성한 게시물·홍보물의 의료법 위반 여부에 대해
                            전적인 책임을 지며, 회사는 이에 대해 어떠한 책임도 지지 않습니다.
                        </p>
                    </Section>

                    {/* 제4-5조: 테스트보철 매칭 이용료·수수료 (v4.0 신설) */}
                    <Section
                        id="section4_5"
                        title="제4조의5 (테스트보철 매칭 서비스 이용료 및 매칭 수수료)"
                        expanded={expandedSections.section4_5}
                        onToggle={() => toggleSection('section4_5')}
                    >
                        <p style={styles.text}>
                            1. 회사는 치과(비회원 신청을 포함한다)의 A/S(테스트보철) 제작 요청을 접수하여 게시판에 게시하고,
                            이를 기공소 회원과 연결하는 매칭 서비스를 제공합니다. 치과·환자 식별 정보는 매칭 성립 전까지 공개되지 않습니다.
                        </p>
                        <p style={styles.text}>
                            2. <strong>건당 이용료(무료)</strong>: 테스트보철 케이스 수락에 따른 건당 매칭 이용료는
                            <strong> 무료(0원)</strong>입니다. 회사는 케이스 수락에 대하여 기공소 회원에게 별도의 이용료를 청구하지 않습니다.
                        </p>
                        <p style={styles.text}>
                            3. <strong>매칭 수수료(무료)</strong>: 회사의 매칭으로 치과와 기공소 간 거래가 성사된 경우에도
                            매칭 수수료는 <strong>무료(0원)</strong>입니다. 회사는 테스트보철 매칭, 해외 거래 중개를 포함한
                            <strong> 일체의 중개 수수료를 받지 않습니다.</strong>
                            다만 회사가 장래 이용료·수수료를 도입하거나 변경하려는 경우, 시행일 30일 전까지
                            서비스 내 공지 등의 방법으로 회원에게 고지하며, 변경에 동의하지 않는 회원은 이용계약을 해지할 수 있습니다.
                        </p>
                        <p style={styles.text}>
                            4. 회사는 매칭·수락·전송 이력을 거래 증빙을 위해 보존하며,
                            이는 분쟁 해결의 근거 자료로 사용됩니다.
                        </p>
                    </Section>

                    {/* 제4-6조: 통신판매중개 지위·디자인외주 (v5.0 신설) */}
                    <Section
                        id="section4_6"
                        title={showV71 ? V71_ART4_6_TITLE : '제4조의6 (중개서비스의 법적 지위 및 국내 디자인외주)'}
                        expanded={expandedSections.section4_6}
                        onToggle={() => toggleSection('section4_6')}
                    >
                        {/* v7.1 판: 제목·제1항 괄호에 외주 제조 추가, 제1항 끝 단서·제3항 끝·제4항 끝 문장 추가(기존 문장 그대로). 문안 termsV71.js */}
                        <p style={styles.text}>
                            1. <strong>통신판매중개자 지위</strong>: 회사가 제공하는 매칭·중개 서비스(테스트보철 매칭,
                            국내 CAD 디자인 외주, {showV71 ? `${V71_ART4_6_P1_PAREN_ITEM}, ` : ''}거래처 연결 등)에서 회사는 <strong>통신판매중개자로서 회원 간 거래를 위한
                            시스템을 운영·관리·제공할 뿐이며, 거래의 당사자가 아닙니다.</strong> 재화·용역의 품질, 하자,
                            대금 정산, 의료기사법 등 관계 법령의 준수에 대한 책임은 거래 당사자인 회원이 직접 부담합니다.
                            {showV71 ? ` ${V71_ART4_6_P1_PROVISO}` : ''}
                        </p>
                        <p style={styles.text}>
                            2. <strong>국내 CAD 디자인 외주</strong>: 회사는 기공소 회원 간 컴퓨터지원설계(CAD) 용역의
                            중개 서비스를 제공합니다. 발주 기공소가 제작·품질·납품 책임을 유지하며, 수주 기공소는
                            설계 데이터 작업만을 수행합니다. 디자인 대금은 당사자 간 직접 정산합니다.
                        </p>
                        <p style={styles.text}>
                            3. <strong>중개 수수료(무료)</strong>: 국내 CAD 디자인 외주 거래의 중개 수수료는 <strong>무료(0원)</strong>입니다.
                            회사는 디자인 외주 중개에 대하여 수주 기공소 회원에게 별도의 수수료를 청구하지 않습니다.
                            다만 회사가 장래 수수료를 도입하거나 변경하려는 경우, 시행일 30일 전까지 서비스 내 공지 등의 방법으로 회원에게 고지합니다.
                            회원은 디자인 외주 거래의 증빙(확정 거래명세서 등)을 관계 법령에 따라 보존합니다.
                            디자인 대금은 당사자 간 직접 정산합니다.
                            {showV71 ? ` ${V71_ART4_6_P3_ADD}` : ''}
                        </p>
                        <p style={styles.text}>
                            4. 회사는 매칭·수락·정산 화면에 회사가 거래의 당사자가 아니라는 사실을 고지합니다.
                            {showV71 ? ` ${V71_ART4_6_P4_ADD}` : ''}
                        </p>
                    </Section>

                    {/* 제4-7조: 환자정보 처리위탁·재위탁 (v5.0 신설 → v7.0 전면 개정) */}
                    {showV70 ? (
                        <Section
                            id="section4_7"
                            title={V70_ART4_7.title}
                            expanded={expandedSections.section4_7}
                            onToggle={() => toggleSection('section4_7')}
                        >
                            <p style={styles.text}>{V70_ART4_7.p1}</p>
                            <ul style={styles.list}>
                                {V70_ART4_7.p1items.map((t) => <li key={t.slice(0, 6)}>{t}</li>)}
                            </ul>
                            {V70_ART4_7.paras.map((t) => <p key={t.slice(0, 6)} style={styles.text}>{t}</p>)}
                            <ul style={styles.list}>
                                {V70_ART4_7.p7items.map((t) => <li key={t.slice(0, 6)}>{t}</li>)}
                            </ul>
                            {V70_ART4_7.paras2.map((t) => <p key={t.slice(0, 6)} style={styles.text}>{t}</p>)}
                            {/* 완성 디자인 A — "완성 산출물 보관·전달" 명문(법적확정안 §1). 시행일(2026-10-21) 전엔 fail-closed 로 숨김. 기존 제4조의7 동의 해시(V70_ART4_7_TEXT)는 변경하지 않는 추가 안내 문단. */}
                            {isPrivacyDesignOn && (
                                <p style={styles.text}>
                                    {'완성 디자인 산출물의 보관·전달: 회원(치과기공소)이 의뢰받은 케이스의 컴퓨터지원설계(CAD)를 마친 완성 디자인 파일(STL 등 3D 모델)과 지그 결과·3D 모델 결과 및 그 결과 사진(PNG 이미지)(이하 합하여 "완성 산출물")을, 그 결과를 생성한 기공소 회원이 웹에서 내려받아 제작에 쓰도록, 회사가 일정 기간 보관하고 전달합니다. 완성 산출물에는 그 파일에 음각(새김)으로 들어가는 환자 이름, 치식(치아 번호), 해당 치과의 이름이 포함될 수 있고, 결과 사진에는 모델 바닥에 새긴 환자 이름이 형상으로 보일 수 있습니다. 전달용 파일(구강 스캔·CAD 중간 작업파일 등 완성 전 자료)은 회사 서버를 거쳐 전달만 하고 보관하지 않으나, 완성 산출물은 회원의 내려받기를 위하여 회사가 보관합니다. 보관 기간은 완성 디자인 파일은 회원이 내려받으면 그 뒤 삭제하고 내려받지 않더라도 생성일부터 14일이 지나면 자동 삭제하며, 지그 결과·3D 모델 결과와 그 결과 사진은 생성일부터 7일이 지나면 자동 삭제합니다(관계 법령에 따른 기간이 아니라 회원과의 위탁 약정에 따른 기간입니다). 환자가 치과나 기공소에 삭제를 요구하면 그 요청에 따라 기간 전이라도 삭제할 수 있습니다. 완성 산출물에 포함된 환자 개인정보의 개인정보처리자(위탁자) 책임과 환자 본인에 대한 동의·고지 확보 책임은 그 환자의 진료·기공을 담당하고 회사에 외주를 맡긴 회원(치과 또는 치과기공소)에게 있고, 회사는 수탁자로서 「개인정보 보호법」 제26조에 따른 안전조치 의무를 부담하며 위탁 목적 밖으로 완성 산출물을 이용하지 않습니다. 치과가 회사의 회원인 경우 완성 산출물의 회사 보관 및 재위탁에 대한 동의는 치과 회원의 가입·이용 약정에 포함되고(제9항의 CAD 재위탁 동의와 함께 적용), 치과가 회사의 회원이 아니고 치과기공소만 회사를 이용하는 경우 그 완성 산출물에 포함된 환자 개인정보에 관한 위탁자 책임은 그 치과기공소가 전적으로 부담합니다.'}
                                </p>
                            )}
                        </Section>
                    ) : (
                    <Section
                        id="section4_7"
                        title="제4조의7 (환자 개인정보의 처리위탁 및 설계 업무 재위탁)"
                        expanded={expandedSections.section4_7}
                        onToggle={() => toggleSection('section4_7')}
                    >
                        <p style={styles.text}>
                            1. <strong>처리위탁</strong>: 회원(의료기관·치과기공소)은 개인정보처리자로서, 의뢰서·거래명세서
                            작성 및 파일 전송에 수반되는 환자 개인정보(성명, 치식, 보철 정보, 구강 스캔 데이터 등)의
                            처리 업무를 회사에 위탁하며, 회사는 수탁자로서 위탁 목적 범위를 넘어 이를 이용하거나
                            제3자에게 제공하지 않습니다. 환자로부터의 수집·이용 동의 확보 책임은 해당 의료기관에 있습니다.
                        </p>
                        <p style={styles.text}>
                            2. <strong>설계 업무 재위탁 동의</strong>: 회원(의료기관)은 기공물 제작을 수탁한 치과기공소가
                            제작 공정 중 컴퓨터지원설계(CAD) 업무에 한하여 관계 법령에 따라 개설등록된 다른
                            치과기공소에 재위탁할 수 있음에 동의합니다. 재위탁하는 경우에도 제작·품질·납품 책임은
                            원수탁 기공소가 유지합니다.
                        </p>
                        <p style={styles.text}>
                            3. <strong>정보 최소화 및 삭제</strong>: 재위탁 및 파일 전송 과정에서 전달되는 정보는 설계에
                            필요한 범위로 최소화하며, 회사의 중계 서버에 일시 저장되는 파일은 수신 확인 후 24시간 이내,
                            늦어도 7일 이내 자동 삭제됩니다. 전송 구간은 암호화됩니다.
                        </p>
                        <p style={styles.text}>
                            4. 수주 기공소는 재위탁받은 환자 정보를 설계 목적 외로 이용할 수 없으며,
                            작업 완료 후 지체 없이 파기하여야 합니다.
                        </p>
                        {/* 완성 디자인 A — "완성 산출물 보관·전달" 명문(법적확정안 §1). 시행일(2026-10-21) 전엔 fail-closed 로 숨김. */}
                        {isPrivacyDesignOn && (
                            <p style={styles.text}>
                                5. <strong>완성 디자인 산출물의 보관·전달</strong>: 회원(치과기공소)이 의뢰받은 케이스의 CAD를 마친 완성 디자인 파일(STL 등)과 지그 결과·3D 모델 결과 및 그 결과 사진(이하 합하여 "완성 산출물")을, 그 결과를 생성한 기공소 회원이 웹에서 내려받아 제작에 쓰도록 회사가 일정 기간 보관하고 전달합니다. 완성 산출물에는 환자 이름·치식·치과 이름이 음각으로 포함될 수 있고, 결과 사진에는 모델 바닥에 새긴 환자 이름이 형상으로 보일 수 있습니다. 전달용 파일은 거쳐 전달만 하고 보관하지 않으나 완성 산출물은 보관하며, 완성 디자인 파일은 회원이 내려받으면 그 뒤 삭제하고 내려받지 않더라도 생성일부터 14일이 지나면 자동 삭제하며, 지그 결과·3D 모델 결과와 결과 사진은 생성일부터 7일이 지나면 자동 삭제합니다(위탁 약정에 따른 기간). 환자가 치과나 기공소에 삭제를 요구하면 그 요청에 따라 기간 전이라도 삭제할 수 있습니다. 완성 산출물에 포함된 환자 개인정보의 위탁자 책임은 외주를 맡긴 회원에게 있고, 회사는 수탁자로서 「개인정보 보호법」 제26조의 안전조치 의무를 부담하며 위탁 목적 밖으로 이용하지 않습니다. 치과가 회원이 아닌 경우 이 책임은 완성본을 만든 치과기공소가 전적으로 부담합니다.
                            </p>
                        )}
                    </Section>
                    )}

                    {/* 제4조의8·제4조의9 — v7.0 신설 */}
                    {showV70 && (
                        <Section
                            id="section4_8"
                            title={V70_ART4_8.title}
                            expanded={expandedSections.section4_8}
                            onToggle={() => toggleSection('section4_8')}
                        >
                            {V70_ART4_8.paras.map((t) => <p key={t.slice(0, 6)} style={styles.text}>{t}</p>)}
                            <ul style={styles.list}>
                                {V70_ART4_8.p11items.map((t) => <li key={t.slice(0, 6)}>{t}</li>)}
                            </ul>
                            <p style={styles.text}>{V70_ART4_8.p12}</p>
                            <ul style={styles.list}>
                                {V70_ART4_8.p12items.map((t) => <li key={t.slice(0, 6)}>{t}</li>)}
                            </ul>
                        </Section>
                    )}
                    {showV70 && (
                        <Section
                            id="section4_9"
                            title={V70_ART4_9.title}
                            expanded={expandedSections.section4_9}
                            onToggle={() => toggleSection('section4_9')}
                        >
                            {V70_ART4_9.paras.map((t) => <p key={t.slice(0, 6)} style={styles.text}>{t}</p>)}
                            <ul style={styles.list}>
                                {V70_ART4_9.p5items.map((t) => <li key={t.slice(0, 6)}>{t}</li>)}
                            </ul>
                            <p style={styles.text}>{V70_ART4_9.p6}</p>
                            <ul style={styles.list}>
                                {V70_ART4_9.p6items.map((t) => <li key={t.slice(0, 6)}>{t}</li>)}
                            </ul>
                        </Section>
                    )}

                    {/* 제4조의10·11·12 — v7.1 신설 (CNC 파트너: 외주 제조 중개·제조업체회원 / 영업업체회원 / 회사의 재판매). 문안 termsV71.js = 정본 §2. v7.0·v6.2 판에는 보이지 않음 */}
                    {showV71 && (
                        <Section
                            id="section4_10"
                            title={V71_ART4_10.title}
                            expanded={expandedSections.section4_10}
                            onToggle={() => toggleSection('section4_10')}
                        >
                            {V71_ART4_10.paras.map((t) => <p key={t.slice(0, 6)} style={styles.text}>{t}</p>)}
                        </Section>
                    )}
                    {showV71 && (
                        <Section
                            id="section4_11"
                            title={V71_ART4_11.title}
                            expanded={expandedSections.section4_11}
                            onToggle={() => toggleSection('section4_11')}
                        >
                            {V71_ART4_11.paras.map((t) => <p key={t.slice(0, 6)} style={styles.text}>{t}</p>)}
                        </Section>
                    )}
                    {showV71 && (
                        <Section
                            id="section4_12"
                            title={V71_ART4_12.title}
                            expanded={expandedSections.section4_12}
                            onToggle={() => toggleSection('section4_12')}
                        >
                            {V71_ART4_12.paras.map((t) => <p key={t.slice(0, 6)} style={styles.text}>{t}</p>)}
                        </Section>
                    )}

                    {/* 제5조 */}
                    <Section
                        id="section5"
                        title="제5조 (회원의 의무)"
                        expanded={expandedSections.section5}
                        onToggle={() => toggleSection('section5')}
                    >
                        <p style={styles.text}>회원은 다음 각 호의 행위를 하여서는 안 됩니다:</p>
                        <ul style={styles.list}>
                            <li>타인의 개인정보 도용 또는 침해</li>
                            <li>서비스 운영을 방해하는 행위</li>
                            <li>허위 정보 기재 또는 게시</li>
                            <li>의료법, 의료기사법 등 관련 법령 위반 행위</li>
                            <li>저작권 등 타인의 지식재산권 침해</li>
                            <li>음란, 폭력, 혐오 등 불건전한 콘텐츠 게시</li>
                            <li>상업적 목적의 무단 광고 게시</li>
                            <li>서비스를 이용한 불법 거래</li>
                            <li>회사의 동의 없는 영리 활동</li>
                        </ul>
                    </Section>

                    {/* 제6조 */}
                    <Section
                        id="section6"
                        title="제6조 (마켓플레이스 거래)"
                        expanded={expandedSections.section6}
                        onToggle={() => toggleSection('section6')}
                    >
                        <h4 style={styles.subTitle}>가. 판매자의 의무</h4>
                        <ul style={styles.list}>
                            <li>정확한 상품 정보 제공</li>
                            <li>주문 확인 후 신속한 배송</li>
                            <li>교환/반품 요청에 대한 성실한 응대</li>
                            <li>관련 법령(전자상거래법 등) 준수</li>
                        </ul>

                        <h4 style={styles.subTitle}>나. 구매자의 의무</h4>
                        <ul style={styles.list}>
                            <li>정확한 배송 정보 제공</li>
                            <li>상품 수령 후 확인</li>
                            <li>정당한 사유 없는 결제 취소/환불 요청 자제</li>
                        </ul>

                        <h4 style={styles.subTitle}>다. 결제 및 정산</h4>
                        <ul style={styles.list}>
                            <li>결제는 포트원(Iamport) 또는 토스페이먼츠 시스템을 통해 처리됩니다.</li>
                            <li>판매자 정산은 회사의 정산 정책에 따릅니다.</li>
                            <li>환불은 각 결제 수단의 환불 정책에 따릅니다.</li>
                        </ul>

                        <div style={styles.warningBox}>
                            <AlertCircle size={18} color="#d97706" />
                            <p style={styles.warningText}>
                                회사는 마켓플레이스에서 판매되는 상품의 품질, 안전성, 적법성에 대해
                                직접적인 책임을 지지 않습니다. 상품에 대한 책임은 판매자에게 있습니다.
                            </p>
                        </div>
                    </Section>

                    {/* 제7조 */}
                    <Section
                        id="section7"
                        title="제7조 (중고물품 거래)"
                        expanded={expandedSections.section7}
                        onToggle={() => toggleSection('section7')}
                    >
                        <p style={styles.text}>
                            1. 중고물품 거래는 회원 간 직접 거래이며, 회사는 거래 당사자가 아닙니다.
                        </p>
                        <p style={styles.text}>
                            2. 회사는 중고물품의 상태, 성능, 안전성에 대해 보증하지 않습니다.
                        </p>
                        <p style={styles.text}>
                            3. 거래 관련 분쟁은 당사자 간 해결을 원칙으로 하며, 회사는 분쟁에 개입하지 않습니다.
                        </p>
                        <p style={styles.text}>
                            4. 회원은 허위 매물 등록, 사기 거래 등 불법 행위를 해서는 안 됩니다.
                        </p>

                        <div style={styles.warningBox}>
                            <AlertCircle size={18} color="#d97706" />
                            <p style={styles.warningText}>
                                중고 의료장비 거래 시 관련 법령(의료기기법 등)을 반드시 준수해야 합니다.
                                허가/신고가 필요한 의료기기는 적법한 절차를 따라야 합니다.
                            </p>
                        </div>
                    </Section>

                    {/* 제8조 */}
                    <Section
                        id="section8"
                        title="제8조 (마케팅 대행 서비스)"
                        expanded={expandedSections.section8}
                        onToggle={() => toggleSection('section8')}
                    >
                        <p style={styles.text}>
                            1. 마케터회원은 의뢰받은 마케팅 콘텐츠를 성실히 제작하고 발행해야 합니다.
                        </p>
                        <p style={styles.text}>
                            2. 콘텐츠 제작 시 의료광고 관련 법령(의료법, 표시광고법 등)을 준수해야 합니다.
                        </p>
                        <p style={styles.text}>
                            3. 의뢰인의 승인 없이 콘텐츠를 발행해서는 안 됩니다.
                        </p>
                        <p style={styles.text}>
                            4. 마케팅 서비스에 대한 보수 및 정산은 별도 계약에 따릅니다.
                        </p>
                    </Section>

                    {/* 제9조 */}
                    <Section
                        id="section9"
                        title="제9조 (회사의 의무 및 책임 제한)"
                        expanded={expandedSections.section9}
                        onToggle={() => toggleSection('section9')}
                    >
                        <h4 style={styles.subTitle}>가. 회사의 의무</h4>
                        <ul style={styles.list}>
                            <li>안정적인 서비스 제공을 위해 노력합니다.</li>
                            <li>개인정보보호를 위해 최선을 다합니다.</li>
                            <li>회원의 의견을 수렴하여 서비스를 개선합니다.</li>
                            <li>서비스 장애 시 신속히 복구하도록 노력합니다.</li>
                        </ul>

                        <h4 style={styles.subTitle}>나. 책임의 제한</h4>
                        <ul style={styles.list}>
                            <li>회사는 회원 간 거래에 직접 관여하지 않으며, 거래 당사자 간 분쟁에 대해 책임을 지지 않습니다.</li>
                            <li>기공물의 품질, 진료 결과, 상품의 하자에 대해 책임을 지지 않습니다.</li>
                            <li>중고물품의 상태, 성능에 대해 책임을 지지 않습니다.</li>
                            <li>마케팅 콘텐츠로 인한 법적 분쟁에 대해 책임을 지지 않습니다.</li>
                            <li>천재지변, 전쟁, 기타 불가항력으로 인한 서비스 중단에 대해 책임을 지지 않습니다.</li>
                            <li>회원의 귀책사유로 인한 서비스 이용 장애에 대해 책임을 지지 않습니다.</li>
                        </ul>

                        <div style={styles.infoBox}>
                            <p style={styles.infoText}>
                                <strong>중요:</strong> 본 플랫폼은 의료행위를 중개하거나 의료기관을 추천하지 않습니다.
                                플랫폼은 업무 관리 도구와 거래 플랫폼을 제공할 뿐이며,
                                실제 진료, 기공물 제작, 상품의 품질에 대해서는 해당 당사자가 전적인 책임을 집니다.
                                {showV70 && V70_INFOBOX_EXTRA}
                            </p>
                        </div>
                    </Section>

                    {/* 제10조 */}
                    <Section
                        id="section10"
                        title="제10조 (지식재산권)"
                        expanded={expandedSections.section10}
                        onToggle={() => toggleSection('section10')}
                    >
                        <p style={styles.text}>
                            1. 서비스 내 회사가 제작한 콘텐츠에 대한 저작권 및 지식재산권은 회사에 귀속됩니다.
                        </p>
                        <p style={styles.text}>
                            2. 회원이 게시한 콘텐츠에 대한 권리는 해당 회원에게 있으나,
                            서비스 운영에 필요한 범위 내에서 회사가 이용할 수 있습니다.
                        </p>
                        <p style={styles.text}>
                            3. 회원은 타인의 지식재산권을 침해하는 콘텐츠를 게시해서는 안 됩니다.
                        </p>
                    </Section>

                    {/* 제11조 */}
                    <Section
                        id="section11"
                        title="제11조 (서비스 이용 제한)"
                        expanded={expandedSections.section11}
                        onToggle={() => toggleSection('section11')}
                    >
                        <p style={styles.text}>
                            회사는 회원이 본 약관을 위반하거나 서비스의 정상적인 운영을 방해한 경우,
                            다음과 같은 조치를 취할 수 있습니다:
                        </p>
                        <ul style={styles.list}>
                            <li>경고</li>
                            <li>콘텐츠 삭제 또는 비공개 처리</li>
                            <li>일정 기간 서비스 이용 제한</li>
                            <li>스토어 비공개 또는 판매 중지</li>
                            <li>회원 자격 정지 또는 탈퇴 처리</li>
                        </ul>
                    </Section>

                    {/* 제12조 */}
                    <Section
                        id="section12"
                        title="제12조 (분쟁 해결)"
                        expanded={expandedSections.section12}
                        onToggle={() => toggleSection('section12')}
                    >
                        <p style={styles.text}>
                            1. 회원 간 분쟁이 발생한 경우, 당사자 간 협의로 해결합니다.
                        </p>
                        <p style={styles.text}>
                            2. 협의가 이루어지지 않을 경우, 관련 법령 및 일반 상관례에 따릅니다.
                        </p>
                        <p style={styles.text}>
                            3. 회사와 회원 간 분쟁에 대한 소송은 회사 소재지 관할 법원으로 합니다.
                        </p>
                        <p style={styles.text}>
                            4. 본 약관은 대한민국 법률에 따라 해석됩니다.
                        </p>
                    </Section>

                    {/* 제13조 */}
                    <Section
                        id="section13"
                        title="제13조 (약관의 변경)"
                        expanded={expandedSections.section13}
                        onToggle={() => toggleSection('section13')}
                    >
                        <p style={styles.text}>
                            1. 회사는 관련 법령을 위반하지 않는 범위에서 본 약관을 변경할 수 있습니다.
                        </p>
                        <p style={styles.text}>
                            2. 약관이 변경되는 경우, 시행일 7일 전부터 서비스 내 공지합니다.
                            다만, 회원에게 불리한 변경의 경우 30일 전부터 공지합니다.
                        </p>
                        <p style={styles.text}>
                            3. 변경된 약관에 동의하지 않는 회원은 회원 탈퇴를 할 수 있습니다.
                        </p>
                    </Section>

                    {/* 부칙 */}
                    <div style={styles.appendix}>
                        <h3 style={styles.appendixTitle}>부칙</h3>
                        <p style={styles.text}>
                            1. 본 약관은 2026년 4월 9일부터 시행하며, v5.0 신설·개정 조항(제4조의2 요금, 제4조의6, 제4조의7)은 2026년 8월 2일부터 시행됩니다.
                            v5.1 개정 조항(제4조의6 제3항, 국내 디자인외주 중개 수수료 폐지)은 회원에게 유리한 변경으로 공지와 동시에 2026년 8월 15일부터 즉시 시행됩니다.
                            v6.0 개정 조항은 2026년 8월 17일 공고하여 2026년 9월 17일부터 시행하되,
                            회원에게 유리한 변경인 수수료 폐지(제4조의5 제2항·제3항)는 공고와 동시에 2026년 8월 17일부터 즉시 시행됩니다.
                            치과 회원 구독료(제4조의2)는 v6.1 개정(2026년 8월 21일 공고)에 따라 2026년 8월 21일 이후 신규 가입하는
                            치과 회원에게는 가입과 동시에 적용되며, 그 전에 가입한 기존 치과 회원에게는 최소 30일 전 사전 고지 후 적용됩니다.
                            {` v6.2 개정 조항(제4조의2 구독료·무료 체험·결제 주기)은 ${TERMS_V62_DATE} 공고하여 그날 이후 신규 가입하는 회원에게는 가입 시 동의로 즉시 적용하며, 그 전에 가입한 회원에게는 제4조의2 제6항(경과 규정)에 따릅니다. v6.2 개정 조항 중 결제 실패·이용 중지 시 덴클 프로그램 중지(제4조의2 제3항)는 ${TERMS_V62_DATE} 이후 신규 가입하는 회원에게는 가입 시 동의로 즉시 적용하며, 기존 회원에게는 2026년 11월 3일부터 적용합니다.`}
                            {showV70 && V70_APPENDIX_PARA}
                            {showV71 && V71_APPENDIX_PARA}
                        </p>
                        <p style={styles.text}>
                            2. 본 약관 시행 이전에 가입한 회원에게는 시행일로부터 30일의 고지 기간이 경과한 날부터 적용되며,
                            시행일 이후 신규 가입한 회원에게는 즉시 적용됩니다.
                        </p>
                        <p style={styles.text}>
                            3. <strong>변경 이력</strong>
                        </p>
                        <ul style={styles.list}>
                            <HistoryBaseItems />
                        {showV70 && <p>{V70_HISTORY_LINE}</p>}
                        {showV71 && <p>{V71_HISTORY_LINE}</p>}
                        <HistoryNote1024 />
                        </ul>
                    </div>
                    </>)}
                </div>

                {/* 하단 버튼 */}
                <div style={styles.footer}>
                    <p style={styles.footerNote}>
                        본 이용약관에 동의하시면 서비스 이용이 가능합니다.
                    </p>
                    {onClose && (
                        <button onClick={onClose} style={styles.confirmButton}>
                            확인했습니다
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

// 부칙 변경 이력 — 공통 줄(v1.0~v6.2)과 2026-10-24 정정 줄. 기존 판과 v8.0 판이 같은 요소를 쓴다(v8.0 판은 사이에 v7.0·v7.1·v8.0 줄을 끼운다)
function HistoryBaseItems() {
    return (<>
        <li>v1.0 (2025.11.23): 최초 제정</li>
        <li>v2.0 (2025.12.05): 마켓플레이스, 중고물품, 마케팅 대행 조항 추가</li>
        <li>v3.0 (2026.04.09): 5% 거래 수수료 모델 폐지, 월정액 구독 모델 전환, 환자 리뷰 시스템·환불정책·의료광고 책임 분담 조항 신설</li>
        <li>v4.0 (2026.07.14): 테스트보철(A/S) 매칭 서비스 신설 — 건당 이용료 10,000원, 매칭 거래 수수료 4.5%(부가세 별도), 우회 금지 및 증빙 보존 조항 추가 (제4조의5)</li>
        <p>· v5.0 (2026-08-02): 요금 현행화(기공소 33,000원·1개월 체험), 통신판매중개 지위·국내 디자인외주(제4조의6), 환자정보 처리위탁·재위탁(제4조의7) 신설</p>
        <p>· 2026-08-06: 위치기반서비스 동의의 가입 필수 폐지 (거리순·지도 기능 사용 시점 동의로 전환, 위치기반서비스 이용약관은 공개 디렉토리에 게시)</p>
        <p>· v5.1 (2026-08-15): 국내 CAD 디자인 외주 중개 수수료 폐지(무료화) — 제4조의6 제3항 개정. 회원에게 유리한 변경으로 공지와 동시에 즉시 시행</p>
        <p>· v6.0 (2026-08-17 공고 · 2026-09-17 시행): 중개 수수료 전면 폐지 — 테스트보철 건당 이용료·매칭 수수료를 무료(0원)로 개정하고 해외 중개를 포함한 일체의 중개 수수료를 받지 않음(제4조의5, 유리 변경으로 즉시 시행)</p>
        <p>· v6.0: 치과 회원 구독 신설(월 33,000원, 부가세 포함) — 회사가 별도 공지하는 시행일부터 적용하는 시행 유보 조건, 시행일 전 이용분 무청구(제4조의2)</p>
        <p>· v6.0: 요금 단일화 — 치과·기공소 회원 모두 월 33,000원(부가세 포함) 동일 요금 체계로 정비</p>
        <p>· v6.1 (2026-08-21 공고): 치과 회원 구독의 적용 방식 개정(제4조의2 제5항) — 2026-08-21 이후 신규 가입 치과 회원은 가입과 동시에 적용(비구독 시 일부 기능 제한, 의뢰서 작성·확인은 무료 유지), 기존 치과 회원은 종전대로 최소 30일 전 사전 고지 후 적용</p>
        <p>{`· v6.2 (${TERMS_V62_DATE} 공고): 구독료 개정 — 치과 회원 월 99,000원·기공소 회원 월 33,000원(부가세 포함)으로 이원화(v6.0 요금 단일화는 이 개정으로 대체), 신규 가입 무료 체험 1개월(사업자당 1회), 결제 주기 매월 같은 날, 해지 후 결제 기간까지 이용, 결제 동의 절차 명시, 경과 규정 신설(기존 치과 회원은 별도 통지 시까지 면제, 기존 기공소 회원의 구독료·무료 이용 기간 무변경), 환불 접수 전화번호 변경, 결제 실패 시 결제일부터 7일 유예 후 이용 중지 및 이용 중지 동안 PC 덴클 프로그램의 자료 수집·파일 주고받기 중지와 결제 확인 시 재개, 이용 중지 중 자료 삭제 요청 창구 명시(제4조의2·제4조의3), 결제일부터 7일 이내 구독 화면 직접 환불 신설(계정당 1회, 이후 고객센터 접수)(제4조의3), 환불 접수처에 전자우편 추가(제4조의3), 결제 대행에 토스페이먼츠 명시(제3조·제6조)`}</p>
    </>);
}
function HistoryNote1024() {
    return <p>· 2026-10-24: 제4조의7 완성 산출물 안내 문단 정정 — 지그 결과·3D 모델 결과·결과 사진을 포함하고 그 보관 기간을 7일로 명시(완성 디자인 파일은 14일 유지, 개인정보 처리방침 v3.3.3 개정과 동반)</p>;
}

// 문단 배열 → <p> 나열 (조문 문자열은 전부 {식})
function Paras({ items }) {
    return items.map((t) => <p key={t.slice(0, 12)} style={styles.text}>{t}</p>);
}

// ── v8.0 판 전문 (정본 docs/약관_v8.0_전문_초안_2026-10-24.md §3. 문자열 src/config/termsV80.js) ──
//   제4조의7·제4조의8·제4조의9 = termsV70.js 객체 그대로(해시 보호 — 문자열 복사 없음). 제4조의11 = termsV71.js 그대로. 제4조의10·12 는 termsV80.js 가 V71 배열을 index 재사용.
//   삭제 조(제4조의5·제6조·제7조·제8조)는 「삭제 — 결번」. 부칙: v6.2 문단 + V70 문단 + (ⓑ일 때 V71 문단) + v8.0 문단, 이력도 같은 순서.
//   v71Replaced(ⓐ) 이면 v7.1 은 시행되지 않은 판이라 그 부칙 문단·이력 줄은 넣지 않는다(「11-24 시행」 문장이 사실과 어긋남). 공고 증빙은 v7.1 버튼으로 남긴다.
function V80Articles({ features, expandedSections, toggleSection, v71Replaced }) {
    const sec = (id, title, children) => (
        <Section id={id} title={title} expanded={expandedSections[id]} onToggle={() => toggleSection(id)}>{children}</Section>
    );
    const del = (id, d) => sec(id, d.title, <p style={styles.text}>{d.text}</p>);
    return (<>
        {sec('v80_1', V80_ART1.title, <Paras items={V80_ART1.paras} />)}
        {sec('v80_2', V80_ART2.title, <>
            <p style={styles.text}>{V80_ART2.lead}</p>
            <ul style={styles.list}>
                {V80_ART2.items.map((it) => (
                    <li key={it.text.slice(0, 12)}>{it.text}
                        {it.sub && <ul style={styles.subList}>{it.sub.map((s) => <li key={s.slice(0, 12)}>{s}</li>)}</ul>}
                    </li>
                ))}
            </ul>
        </>)}
        {sec('v80_3', V80_ART3.title, <>
            <p style={styles.text}>{V80_ART3.p1}</p>
            <ul style={styles.list}>{V80_ART3.p1items.map((t) => <li key={t.slice(0, 12)}>{t}</li>)}</ul>
            <Paras items={V80_ART3.paras} />
        </>)}
        {sec('v80_4', V80_ART4.title, <Paras items={V80_ART4.paras} />)}
        {sec('v80_4_2', V80_ART4_2.title, <>
            <p style={styles.text}>{V80_ART4_2.p1}</p>
            <div style={styles.tableBox}>
                <table style={styles.table}>
                    <thead>
                        <tr style={styles.tableHeaderRow}>{V80_ART4_2.tableHead.map((h) => <th key={h} style={styles.tableHeader}>{h}</th>)}</tr>
                    </thead>
                    <tbody>
                        {V80_ART4_2.tableRows.map((row) => (
                            <tr key={row[0]} style={styles.tableRow}>
                                <td style={styles.tableCell}><strong>{row[0]}</strong></td>
                                <td style={styles.tableCell}>{row[1]}</td>
                                <td style={styles.tableCell}>{row[2]}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Paras items={V80_ART4_2.paras} />
        </>)}
        {sec('v80_4_3', V80_ART4_3.title, <Paras items={V80_ART4_3.paras} />)}
        {sec('v80_4_4', V80_ART4_4.title, <Paras items={V80_ART4_4.paras} />)}
        {del('v80_4_5', V80_DELETED_ARTICLES.art4_5)}
        {sec('v80_4_6', V80_ART4_6.title, <Paras items={V80_ART4_6.paras} />)}
        {/* 제4조의7 — v7.0 불변(해시 4cc1ceb7…). termsV70.js 객체 그대로. 종전 「완성 산출물 안내 문단」은 제4조의15 로 승격되어 여기엔 없다 */}
        {sec('v80_4_7', V70_ART4_7.title, <>
            <p style={styles.text}>{V70_ART4_7.p1}</p>
            <ul style={styles.list}>{V70_ART4_7.p1items.map((t) => <li key={t.slice(0, 6)}>{t}</li>)}</ul>
            <Paras items={V70_ART4_7.paras} />
            <ul style={styles.list}>{V70_ART4_7.p7items.map((t) => <li key={t.slice(0, 6)}>{t}</li>)}</ul>
            <Paras items={V70_ART4_7.paras2} />
        </>)}
        {sec('v80_4_8', V70_ART4_8.title, <>
            <Paras items={V70_ART4_8.paras} />
            <ul style={styles.list}>{V70_ART4_8.p11items.map((t) => <li key={t.slice(0, 6)}>{t}</li>)}</ul>
            <p style={styles.text}>{V70_ART4_8.p12}</p>
            <ul style={styles.list}>{V70_ART4_8.p12items.map((t) => <li key={t.slice(0, 6)}>{t}</li>)}</ul>
        </>)}
        {sec('v80_4_9', V70_ART4_9.title, <>
            <Paras items={V70_ART4_9.paras} />
            <ul style={styles.list}>{V70_ART4_9.p5items.map((t) => <li key={t.slice(0, 6)}>{t}</li>)}</ul>
            <p style={styles.text}>{V70_ART4_9.p6}</p>
            <ul style={styles.list}>{V70_ART4_9.p6items.map((t) => <li key={t.slice(0, 6)}>{t}</li>)}</ul>
        </>)}
        {sec('v80_4_10', V80_ART4_10.title, <Paras items={V80_ART4_10.paras} />)}
        {sec('v80_4_10_2', V80_ART4_10_2.title, <Paras items={V80_ART4_10_2.paras} />)}   {/* S5 (다) 신설 — 외주 제작을 수주하는 기공소회원 */}
        {sec('v80_4_11', V71_ART4_11.title, <Paras items={V71_ART4_11.paras} />)}
        {sec('v80_4_12', V80_ART4_12.title, <Paras items={V80_ART4_12.paras} />)}
        {sec('v80_4_13', V80_ART4_13.title, <Paras items={V80_ART4_13.paras} />)}
        {sec('v80_4_14', V80_ART4_14.title, <Paras items={V80_ART4_14.paras} />)}
        {sec('v80_4_15', V80_ART4_15.title, <Paras items={V80_ART4_15.paras} />)}
        {sec('v80_4_16', V80_ART4_16.title, <>
            <Paras items={V80_ART4_16.paras1to3} />
            <ul style={styles.list}>{V80_ART4_16.p3items.map((t) => <li key={t.slice(0, 6)}>{t}</li>)}</ul>
            <Paras items={V80_ART4_16.paras4to9} />
        </>)}
        {sec('v80_4_17', V80_ART4_17.title, <Paras items={V80_ART4_17.paras} />)}
        {sec('v80_4_18', V80_ART4_18.title, <Paras items={V80_ART4_18.paras} />)}
        {sec('v80_5', V80_ART5.title, <Paras items={V80_ART5.paras} />)}
        {del('v80_6', V80_DELETED_ARTICLES.art6)}
        {del('v80_7', V80_DELETED_ARTICLES.art7)}
        {del('v80_8', V80_DELETED_ARTICLES.art8)}
        {sec('v80_9', V80_ART9.title, <>
            <h4 style={styles.subTitle}>{V80_ART9.dutiesTitle}</h4>
            <ul style={styles.list}>{V80_ART9.duties.map((t) => <li key={t.slice(0, 12)}>{t}</li>)}</ul>
            <h4 style={styles.subTitle}>{V80_ART9.limitsTitle}</h4>
            <ul style={styles.list}>{V80_ART9.limits.map((t) => <li key={t.slice(0, 12)}>{t}</li>)}</ul>
            <div style={styles.infoBox}>
                <p style={styles.infoText}><strong>{V80_ART9.infoLead}</strong> {V80_ART9.infoText}</p>
            </div>
        </>)}
        {sec('v80_10', V80_ART10.title, <Paras items={V80_ART10.paras} />)}
        {sec('v80_11', V80_ART11.title, <>
            <p style={styles.text}>{V80_ART11.lead}</p>
            <ul style={styles.list}>{V80_ART11.items.map((t) => <li key={t}>{t}</li>)}</ul>
            <p style={styles.text}>{V80_ART11.tail}</p>
        </>)}
        {sec('v80_12', V80_ART12.title, <Paras items={V80_ART12.paras} />)}
        {sec('v80_13', V80_ART13.title, <Paras items={V80_ART13.paras} />)}

        {/* 부칙 */}
        <div style={styles.appendix}>
            <h3 style={styles.appendixTitle}>부칙</h3>
            <p style={styles.text}>
                {V80_APPENDIX_P1_BASE}
                {V70_APPENDIX_PARA}
                {!v71Replaced && V71_APPENDIX_PARA}
                {v80AppendixPara(features, v71Replaced)}
            </p>
            <p style={styles.text}>{V80_APPENDIX_P2}</p>
            <p style={styles.text}>{V80_APPENDIX_P3}</p>
            <p style={styles.text}>{V80_APPENDIX_P4}</p>
            <p style={styles.text}><strong>{V80_APPENDIX_P5_TITLE}</strong></p>
            <ul style={styles.list}>
                <HistoryBaseItems />
                <p>{V70_HISTORY_LINE}</p>
                {!v71Replaced && <p>{V71_HISTORY_LINE}</p>}
                <HistoryNote1024 />
                <p>{v80HistoryLine(features)}</p>
            </ul>
        </div>
    </>);
}

// 접을 수 있는 섹션 컴포넌트
function Section({ id, title, children, expanded, onToggle }) {
    return (
        <div style={styles.section}>
            <button onClick={onToggle} style={styles.sectionHeader}>
                <h3 style={styles.sectionTitle}>{title}</h3>
                {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
            {expanded && (
                <div style={styles.sectionContent}>
                    {children}
                </div>
            )}
        </div>
    );
}

const styles = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px',
    },
    modal: {
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '900px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '24px 32px',
        borderBottom: '2px solid #e5e7eb',
        backgroundColor: '#f8fafc',
    },
    headerTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
    },
    title: {
        margin: 0,
        fontSize: '24px',
        fontWeight: '700',
        color: '#1e293b',
    },
    closeButton: {
        background: 'none',
        border: 'none',
        color: '#64748b',
        cursor: 'pointer',
        padding: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '8px',
    },
    content: {
        padding: '32px',
        overflowY: 'auto',
        flex: 1,
    },
    intro: {
        marginBottom: '24px',
        padding: '20px',
        backgroundColor: '#f1f5f9',
        borderLeft: '4px solid #6366f1',
        borderRadius: '8px',
    },
    introText: {
        margin: 0,
        fontSize: '14px',
        color: '#475569',
        lineHeight: '1.7',
    },
    versionBox: {
        marginBottom: '24px',
        padding: '16px',
        backgroundColor: '#f8fafc',
        borderRadius: '8px',
        fontSize: '14px',
        color: '#64748b',
    },
    // v7.0·v7.1 공고 상자 (판 전환 버튼 줄은 상자 아래 별도)
    noticeBox: {
        marginBottom: '16px',
        padding: '14px 16px',
        backgroundColor: '#eff6ff',
        border: '1px solid #bfdbfe',
        borderRadius: '8px',
        fontSize: '14px',
        lineHeight: 1.6,
    },
    verBtn: {
        padding: '6px 12px',
        backgroundColor: '#ffffff',
        color: '#1e40af',
        border: '1px solid #93c5fd',
        borderRadius: '6px',
        fontSize: '13px',
        fontWeight: 600,
        cursor: 'pointer',
    },
    verBtnActive: {
        padding: '6px 12px',
        backgroundColor: '#1e40af',
        color: '#ffffff',
        border: '1px solid #1e40af',
        borderRadius: '6px',
        fontSize: '13px',
        fontWeight: 600,
        cursor: 'default',
    },
    section: {
        marginBottom: '16px',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        overflow: 'hidden',
    },
    sectionHeader: {
        width: '100%',
        padding: '16px 20px',
        backgroundColor: '#f8fafc',
        border: 'none',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        cursor: 'pointer',
    },
    sectionTitle: {
        margin: 0,
        fontSize: '16px',
        fontWeight: '600',
        color: '#1e293b',
        textAlign: 'left',
    },
    sectionContent: {
        padding: '24px',
        backgroundColor: '#ffffff',
    },
    subTitle: {
        margin: '16px 0 12px 0',
        fontSize: '15px',
        fontWeight: '600',
        color: '#334155',
    },
    text: {
        margin: '0 0 12px 0',
        fontSize: '14px',
        color: '#475569',
        lineHeight: '1.7',
    },
    list: {
        margin: '8px 0',
        paddingLeft: '24px',
        fontSize: '14px',
        color: '#475569',
        lineHeight: '1.8',
    },
    subList: {
        margin: '8px 0',
        paddingLeft: '20px',
        fontSize: '13px',
        color: '#64748b',
        lineHeight: '1.8',
    },
    warningBox: {
        display: 'flex',
        gap: '12px',
        padding: '16px',
        backgroundColor: '#fef3c7',
        border: '1px solid #fcd34d',
        borderRadius: '8px',
        marginTop: '16px',
    },
    warningText: {
        margin: 0,
        fontSize: '13px',
        color: '#92400e',
        lineHeight: '1.6',
    },
    infoBox: {
        padding: '16px',
        backgroundColor: '#eff6ff',
        border: '1px solid #bfdbfe',
        borderRadius: '8px',
        marginTop: '16px',
    },
    infoText: {
        margin: 0,
        fontSize: '13px',
        color: '#1e40af',
        lineHeight: '1.6',
    },
    appendix: {
        marginTop: '24px',
        padding: '20px',
        backgroundColor: '#f8fafc',
        borderRadius: '8px',
    },
    appendixTitle: {
        margin: '0 0 16px 0',
        fontSize: '18px',
        fontWeight: '600',
        color: '#1e293b',
    },
    footer: {
        padding: '24px 32px',
        borderTop: '2px solid #e5e7eb',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        alignItems: 'center',
        backgroundColor: '#f8fafc',
    },
    footerNote: {
        margin: 0,
        fontSize: '13px',
        color: '#64748b',
        textAlign: 'center',
    },
    confirmButton: {
        padding: '12px 48px',
        backgroundColor: '#6366f1',
        color: '#ffffff',
        border: 'none',
        borderRadius: '8px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
    },
};

export default TermsOfService;